import Analytics from '@opencare/analytics';
import { OcsAgentIntegrationType } from '@opencare/shared-types/models/clinic/ocs/enums/OcsAgentIntegrationType';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { LinkButton } from 'brand-guidelines/Buttons-styled-components';
import { COUNTRY } from 'common/constants';
import { ValidationRulePreset } from 'common/Validator/ValidationRules';
import { createImmutableFormValidatorConfig, useFormValidator } from 'common/Validator/Validator';
import { requestClinic } from 'connect/actions';
import { NPR_ERROR_TYPES } from 'connect/constants';
import { RequestAppointment } from 'connect/saga/handlers/appointment/request-appointment';
import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';
import { ValidateReservedAppointmentSlot } from 'connect/saga/handlers/reserved-appointment-slot/validate-slot';
import { selectDataPatient, selectDataUser } from 'connect/selectors';
import { Checkbox as FormCheckbox } from 'pages/checkout/components/common/checkbox-styled-component';
import { PrivacyLink, TermsOfServiceLink } from 'pages/checkout/components/common/ExternalLinks';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { StepHeader, StepHeaderSubtext, StepHeaderText } from 'pages/checkout/components/common/layout-styled-components';
import { DisclaimerText } from 'pages/checkout/components/common/typographic-styled-components';
import { selectRequestedAppointment } from 'pages/checkout/connect/legacy-selectors';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState, PreferredContactMethod, PreferredContactMethods } from 'pages/checkout/connect/types';
import { useProviderOrPracticeDetails } from 'pages/checkout/hooks/useProviderOrPracticeDetails';
import { useIsOfferAllowed } from 'pages/checkout/steps/Confirm/hooks/offer-hooks';
import { formatClinicAddress } from 'pages/checkout/utils/addressUtils';
import { formatPhoneNumber } from 'pages/checkout/utils/phoneUtils';
import { formatTimeSlot } from 'pages/checkout/utils/timeUtils';
import { getTimeFromStartTime } from 'pages/common/helpers/appointment-slot-helpers';
import { getInNetworkStatus } from 'pages/common/helpers/common-helpers';
import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { selectDataClinic } from 'pages/profile/connect/selectors';

import OpencareRewardsModal from 'pages/results/components/OpencareRewardsModal/OpencareRewardsModal';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useOfferService } from 'services/OfferService';
import { useRedirectionService } from 'services/RedirectionService';
import { Clinic } from 'types/Clinic';
import { Provider } from 'types/Provider';
import cjAffiliate from 'utils/cjAffiliate';
import { ExperimentName, EXPERIMENTS } from 'utils/constants/experiment-configs';
import { useEffectOnce } from 'utils/customHooks/useEffectOnce';
import { useExperiment } from 'utils/customHooks/useExperiment';
import { usePatch } from 'utils/customHooks/usePatch';
import { googlePlaceActionLink } from 'utils/google';
import Logger, { useLogger } from 'utils/Logger';

import { SecurityBadgesLogos } from '../Account/components/SecurityBadges/SecurityBadgesLogos';
import { FormRow } from '../Insurance/Insurance-styled-components';

import { DetailItem, Details } from './components/DetailItem/DetailItem';
import { ErrorMessage } from './components/ErrorMessage';
import { NoAvailabilityModal } from './components/NoAvailabilityModal/NoAvailabilityModal';
import { OfferAcknowledgementCard } from './components/OfferAcknowledgementCard/OfferAcknowledgementCard';
import { PromoCodeBox } from './components/PromoCodeBox/PromoCodeBox';
import { InsuranceStatusBadge, NoOffersStatusBadge } from './components/StatusBadges/ConfirmStepStatusBadge';
import {
  AcknowledgementsWrapper,
  CheckboxLabeledInput,
  ConfirmStepWrapper,
  DetailsWrapper,
  GiftCardText,
  PracticeDetails,
  PracticeTextWrapper,
  ProfilePhoto,
  StatusBadgesWrapper,
  SubmitRequestButton,
  SubmitRequestWrapper,
  SubmitWrapper,
} from './Confirm-styled-components';
import { getOfferedAppointmentType, getPatientAppUrl } from './helpers';
import { useAppointmentRequestData } from './hooks/request-hooks';

interface RequestedAppointment {
  slotId: string;
  provider: Provider;
}

interface ShouldIgnoreParams {
  showOfferAcknowledgement: boolean;
  isOfferAllowed: boolean;
}

interface SlotValidationResult {
  isSlotAvailable: boolean;
}

const OFFER_ACKNOWLEDGEMENT_VALIDATOR_CONFIG = createImmutableFormValidatorConfig([
  {
    field: 'acceptsTerms',
    preset: ValidationRulePreset.isTrue,
    shouldIgnore: ({ showOfferAcknowledgement }: ShouldIgnoreParams) => !showOfferAcknowledgement,
  },
  {
    field: 'acceptsGiftCardOfferedByOpencare',
    preset: ValidationRulePreset.isTrue,
    shouldIgnore: ({ showOfferAcknowledgement }: ShouldIgnoreParams) => !showOfferAcknowledgement,
  },
  {
    field: 'acceptsNoOffer',
    preset: ValidationRulePreset.isTrue,
    shouldIgnore: ({ isOfferAllowed }: ShouldIgnoreParams) => isOfferAllowed,
  },
]);

const OFFER_OFFERED_BY_OPENCARE_LABEL = 'I understand this gift card is offered by Opencare, not the dental practice. For any questions, I will contact Opencare.';

const OFFER_ELIGIBILITY_ACKNOWLEDGEMENT_THRESHOLD = 50;

const contactOptions = [
  { label: 'Phone Call', contactMethod: PreferredContactMethod.Phone },
  { label: 'SMS', contactMethod: PreferredContactMethod.SMS },
];

const contactOptionToApiPayload = (contactMethods: PreferredContactMethods) =>
  Object.keys(contactMethods)
    .filter((key) => contactMethods[key as PreferredContactMethod]);

const analytics = new Analytics();

export const Confirm = () => {
  const logger = useLogger();

  const dispatch = useDispatch();
  const analyticsService = useAnalyticsService();

  const { isVariant: isSecurityMessagingVariant } = useExperiment({
    experiment: EXPERIMENTS[ExperimentName.CHECKOUT_SECURITY_MESSAGING],
  });

  const patchCheckout = usePatch<CheckoutState>({
    stateHandler: CheckoutStateHandler,
  });

  const [isLoadingPatientApp, setIsLoadingPatientApp] = useState(false);

  const funnelStorage = useFunnelStorage();
  const redirectionService = useRedirectionService();

  const appointmentRequestData = useAppointmentRequestData();
  const { providerId, body: appointmentRequestBody } = appointmentRequestData || {};

  const isOfferAllowed = useIsOfferAllowed();

  const user = useSelector(selectDataUser);
  const patient = useSelector(selectDataPatient);
  const clinic = useSelector<any, Clinic>(selectDataClinic);
  const requestedAppointment = useSelector<any, RequestedAppointment>(selectRequestedAppointment);
  const { slotId } = requestedAppointment;
  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const {
    insurance,
    confirm: confirmState,
  } = checkoutState;
  const { plan: insurancePlan } = insurance || {};

  const requestOfferLoading = useSelector(RequestOffer.loading!.selector);

  const requestAppointmentLoading = useSelector(RequestAppointment.loading!.selector);
  const requestAppointmentError = useSelector(RequestAppointment.error!.selector);
  const newAppointment = useSelector(RequestAppointment.value!.selector);

  const validateSlotLoading = useSelector(ValidateReservedAppointmentSlot.loading!.selector);
  const validateSlotError = useSelector(ValidateReservedAppointmentSlot.error!.selector);
  const validateSlotResult = useSelector(ValidateReservedAppointmentSlot.value!.selector);

  const requestedAppointmentType = getOfferedAppointmentType(requestedAppointment.provider);

  useEffect(() => {
    if (patient?.id && (!patient.emailVerifiedAt || !patient.phoneVerifiedAt)) {
      logger.error('User reached Confirm step without verifying email or phone. NPR creation will fail.', { patient });
    }
  }, [patient, logger]);

  const [submitting, setSubmitting] = useState(false);
  const validateSlotResolver = useRef<((result: SlotValidationResult) => void) | null>(null);

  // Handle slot validation result
  useEffect(() => {
    if (
      validateSlotLoading
      || !validateSlotResult
      || typeof validateSlotResolver.current !== 'function'
    ) {
      return;
    }

    // Slot validated, call resolver with the result
    // This will fulfill the promise started in `handleSlotAvailability`
    validateSlotResolver.current(validateSlotResult);
    validateSlotResolver.current = null;
  }, [validateSlotLoading, validateSlotResult, validateSlotResolver]);

  const isClinicUS = clinic.address.country === COUNTRY.unitedStates;

  const isInNetwork = useMemo(() => {
    if (!insurancePlan?.id) {
      return false;
    }

    return getInNetworkStatus(
      insurancePlan.id,
      clinic.insurancePlans,
    );
  }, [
    insurancePlan?.id,
    clinic.insurancePlans,
  ]);

  const selectedSlot = useMemo(() => {
    const slot = clinic.slots?.find((slot: any) => slot.id === slotId);
    if (!slot) {
      return null;
    }
    return slot;
  }, [clinic.slots, slotId]);

  const {
    preferredContactMethods: storedPreferredContactMethods,
    note: storedNote,
  } = confirmState || {};

  const [preferredContactMethods, setPreferredContactMethods] =
    useState<PreferredContactMethods>(storedPreferredContactMethods || []);
  const [note, setNote] = useState(storedNote || '');

  const formattedAddress = useMemo(() => formatClinicAddress(clinic.address), [clinic.address]);
  const formattedTimeSlot = useMemo(() => {
    if (!selectedSlot?.date || !selectedSlot?.time) {
      return '';
    }
    return formatTimeSlot(selectedSlot.date, selectedSlot.time);
  }, [selectedSlot?.date, selectedSlot?.time]);

  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      ...analyticsService.analyticsData,
    });
  });

  const isSubmitLoading = useMemo(
    () => validateSlotLoading
      || requestAppointmentLoading
      || requestOfferLoading
      || isLoadingPatientApp,
    [validateSlotLoading, requestAppointmentLoading, requestOfferLoading, isLoadingPatientApp],
  );

  const togglePreferredContactMethod = (method: PreferredContactMethod) => {
    if (!method) {
      return;
    }

    setPreferredContactMethods((prev) => {
      const newPreferredContactMethods = { ...prev };
      newPreferredContactMethods[method] = !newPreferredContactMethods[method];
      return newPreferredContactMethods;
    });

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      content: {
        action: 'preferred-contact-toggle',
        contact_method: String(method),
      },
    });
  };

  const preferredAppointmentLabel = formattedTimeSlot
    ? 'Preferred Appointment Time'
    : 'Preferred Appointment Type';

  const requestedAppointmentTypeName = requestedAppointmentType?.appointmentType?.name
    || requestedAppointmentType?.name;
  const showPreferredAppointmentTime = formattedTimeSlot || requestedAppointmentTypeName;

  // Handle failed appointment request
  useEffect(() => {
    if (!requestAppointmentError) {
      return;
    }

    if (requestAppointmentError === NPR_ERROR_TYPES.OCS_WRITE_ERROR) {
      setIsNoAvailabilityModalOpen(true);
    }
  }, [requestAppointmentError]);

  // Handle successful appointment request
  useEffect(() => {
    if (!newAppointment?.id || !submitting || requestAppointmentLoading || requestAppointmentError) {
      return;
    }

    try {
      setSubmitting(false);
      setIsLoadingPatientApp(true);

      const { requestData, reservedSlotDetails } = appointmentRequestBody || {};

      analytics.trackConversion({
        pageType: analytics.pageTypes.CHECKOUT,
        pageName: clinic.name,
        name: 'form-booking',
        section: 'confirm',
        content: {
          first: String(patient?.firstName),
          last: String(patient?.lastName),
          birth: String(patient?.dateOfBirth),
          email: String(patient?.email),
          phone: String(patient?.mobilePhone),
          promo_code: String(requestData?.dmProps?.promo_code),
          date: String(reservedSlotDetails?.date),
          time: reservedSlotDetails?.time ? String(getTimeFromStartTime(reservedSlotDetails.time, 'HHmm')) : '',
          reco_appointment_booked: String(true),
          reco_step_reached: 'Booked',
        },
        userId: user?.id,
        patientId: patient?.id,
        source: requestData?.source,
        appointmentId: newAppointment.id,
        practiceId: newAppointment.provider.clinic.id,
        providerId: newAppointment.provider.id,
        partnerId: user?.partner?.id,
        isReturningPatient: !newAppointment.requestData?.visitedBefore,
        funnelConfigId: newAppointment?.match?.funnelConfiguration?.id,
        matchId: newAppointment?.match?.id,
        offerValue: newAppointment?.offer?.cost,
        appointmentPrice: newAppointment.metadata?.billingPrice,
        clinicScheduleRate: newAppointment.metadata?.clinicScheduleRate,
        clinicCompleteRate: newAppointment.metadata?.clinicCompleteRate,
      });

      cjAffiliate.trackAppointmentConversion(newAppointment);

      // Clear marketing values in funnel now that they've been attached to an appointment
      funnelStorage?.clearMarketingCache();

      // Clear the prefill data.
      funnelStorage?.clearPrefillData();

      googlePlaceActionLink.resetConversionData();
    } catch (error) {
      logger.error('Error tracking conversion', { error });
    } finally {
      // This is in the `finally` block because we never want to block the user from being redirected to the patient app
      redirectionService.redirect({
        url: getPatientAppUrl(newAppointment.id),
        reason: 'appointment-confirmation',
      });
    }
  }, [
    logger,
    submitting,
    requestAppointmentError,
    requestAppointmentLoading,
    appointmentRequestBody,
    newAppointment,
    requestedAppointment,
    patient,
    user?.id,
    user?.partner?.id,
    clinic.id,
    clinic.name,
    clinic.address,
    clinic.primaryPhoto,
    funnelStorage,
    redirectionService,
  ]);

  const patientName = useMemo(() => {
    const nameArray = [patient.firstName, patient.lastName].filter(Boolean);
    return nameArray.join(' ');
  }, [patient.firstName, patient.lastName]);

  const formattedPhoneNumber = useMemo(() => {
    return formatPhoneNumber(patient.mobilePhone);
  }, [patient.mobilePhone]);

  const { photoUrl: profilePhotoUrl, providerName, practiceName, showProvider } = useProviderOrPracticeDetails();

  // Show provider name if provider is selected, otherwise show practice name
  const nameToShow = useMemo(
    () => showProvider ? providerName : practiceName,
    [showProvider, providerName, practiceName],
  );

  const { hasActiveOffer, offerValue, offerName, isThirdPartyOffer } = useOfferService();

  const isOfferAboveThreshold = useMemo(
    () => hasActiveOffer && offerValue >= OFFER_ELIGIBILITY_ACKNOWLEDGEMENT_THRESHOLD,
    [hasActiveOffer, offerValue],
  );

  const { isVariant: isOfferAcknowledgementVariant, variant } = useExperiment({
    experiment: EXPERIMENTS[ExperimentName.OFFER_ELIGIBILITY_ACKNOWLEDGEMENT],
    condition: isOfferAboveThreshold && isOfferAllowed,
  });

  const offerAcknowledgementVariantNumber = variant || null;

  const [isNoAvailabilityModalOpen, setIsNoAvailabilityModalOpen] = useState(false);

  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const [acceptsGiftCardOfferedByOpencare, setAcceptsGiftCardOfferedByOpencare] = useState(false);
  const [opencareRewardsModalVisible, setOpencareRewardsModalVisible] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [acceptsNoOffer, setAcceptsNoOffer] = useState(false);

  const offerTermsLabel = useMemo(() => (
    <span>
      I understand that I must do a new patient exam and cleaning
      at the practice to be eligible for the <GiftCardText>${offerValue} gift card</GiftCardText>,
      and that
      {' '}
      <LinkButton
        $isUnderlined
        $isBold
        onClick={() => setOpencareRewardsModalVisible(true)}
      >
        additional requirements apply.
      </LinkButton>
    </span>
  ), [offerValue]);

  const noOfferTermsLabel = useMemo(() => (
    <span>
      I understand this practice does not participate in Opencare’s reward program and that
      I will not receive a reward for this visit.
    </span>
  ), []);

  const showOfferAcknowledgement = useMemo(() => {
    if (!isOfferAcknowledgementVariant) {
      return false;
    }

    return isOfferAboveThreshold;
  }, [isOfferAcknowledgementVariant, isOfferAboveThreshold]);

  const shouldShowPromoCodeBox = useMemo(() => {
    // Show PromoCodeBox only in variant 1 or control (not variant 2)
    return isOfferAllowed && offerAcknowledgementVariantNumber !== '2';
  }, [isOfferAllowed, offerAcknowledgementVariantNumber]);

  const offerAcknowledgementFormValidator = useFormValidator(OFFER_ACKNOWLEDGEMENT_VALIDATOR_CONFIG);

  const formValidity = useMemo(() => {
    const validationResult = offerAcknowledgementFormValidator.validate(
      { acceptsTerms, acceptsGiftCardOfferedByOpencare, acceptsNoOffer },
      { showOfferAcknowledgement, isOfferAllowed },
    );

    return validationResult;
  }, [
    acceptsTerms,
    acceptsGiftCardOfferedByOpencare,
    showOfferAcknowledgement,
    offerAcknowledgementFormValidator,
    acceptsNoOffer,
    isOfferAllowed,
  ]);

  // Validate slot availability if the clinic is communication-restricted and the integration type is not OCS
  const shouldValidateSlotAvailability = useMemo(
    () => Boolean(
      // Should we validate?
      clinic.isInteractionRestricted
      // Can we validate?
      && selectedSlot?.id
      && selectedSlot?.type !== 'Opencare Automatic'
      && clinic.ocsMetadata?.agent?.integrationType
      && clinic.ocsMetadata?.agent?.integrationType !== OcsAgentIntegrationType.OCS,
    ),
    [
      clinic.isInteractionRestricted,
      clinic.ocsMetadata?.agent?.integrationType,
      selectedSlot?.id,
      selectedSlot?.type,
    ],
  );

  const handleSlotAvailability = useCallback(async () => {
    if (!shouldValidateSlotAvailability) {
      return true;
    }

    // Dispatch the validation action
    dispatch(ValidateReservedAppointmentSlot.createAction({
      providerId,
      // We checked that selectedSlot.id is truthy in shouldValidateSlotAvailability
      slotId: selectedSlot!.id,
    }));

    // resolve will be called in useEffect when the saga completes with the result
    const { isSlotAvailable } = await new Promise<SlotValidationResult>(
      (resolve) => validateSlotResolver.current = resolve,
    );

    // Slot is not available, refresh clinic data from the API and open modal
    if (!isSlotAvailable) {
      dispatch(requestClinic(clinic.id));
      setIsNoAvailabilityModalOpen(true);
    }

    return isSlotAvailable;
  }, [
    shouldValidateSlotAvailability,
    providerId,
    selectedSlot,
    dispatch,
    clinic,
  ]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitLoading) {
      return;
    }

    if (!formValidity.allValid) {
      setShowValidationErrors(true);
      return;
    }

    if (!appointmentRequestBody) {
      Logger.error('No appointment request body found when attempting to submit NPR');
      return;
    }

    const isSlotAvailable = await handleSlotAvailability();
    if (!isSlotAvailable) {
      return;
    }

    patchCheckout({
      confirm: {
        preferredContactMethods,
        note,
      },
    });

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      content: {
        note_provided: String(!!note),
        preferred_contact_phone: String(preferredContactMethods[PreferredContactMethod.Phone] || false),
        preferred_contact_sms: String(preferredContactMethods[PreferredContactMethod.SMS] || false),
      },
    });

    setSubmitting(true);

    // `note` and `contactMethods` are set in this handleSubmit function,
    // so there isn't time for them to be updated in the requestData redux before the request is made
    const newRequestData = {
      note: note || '',
      contactMethods: contactOptionToApiPayload(preferredContactMethods),
    };

    // Avoid mutating the original object
    const updatedAppointmentRequestBody = structuredClone(appointmentRequestBody);
    Object.assign(updatedAppointmentRequestBody.requestData, newRequestData);

    dispatch(RequestAppointment.createAction({
      providerId,
      body: updatedAppointmentRequestBody,
    }));
  }, [
    providerId,
    appointmentRequestBody,
    dispatch,
    formValidity.allValid,
    handleSlotAvailability,
    isSubmitLoading,
    note,
    patchCheckout,
    preferredContactMethods,
  ]);

  const renderOfferAndAcknowledgement = useCallback(() => {
    if (!showOfferAcknowledgement) {
      return null;
    }

    if (offerAcknowledgementVariantNumber === '1') {
      return (
        <AcknowledgementsWrapper>
          <CheckboxLabeledInput label={offerTermsLabel} htmlFor="offer-terms">
            <FormCheckbox
              id="offer-terms"
              checked={acceptsTerms}
              onChange={(e) => setAcceptsTerms(e.target.checked)}
            />
          </CheckboxLabeledInput>
          {showValidationErrors && formValidity.acceptsTerms?.message && (
            <FormError>{formValidity.acceptsTerms.message}</FormError>
          )}

          <CheckboxLabeledInput label={OFFER_OFFERED_BY_OPENCARE_LABEL} htmlFor="offer-opencare">
            <FormCheckbox
              id="offer-opencare"
              checked={acceptsGiftCardOfferedByOpencare}
              onChange={(e) => setAcceptsGiftCardOfferedByOpencare(e.target.checked)}
            />
          </CheckboxLabeledInput>
          {showValidationErrors && formValidity.acceptsGiftCardOfferedByOpencare?.message && (
            <FormError>{formValidity.acceptsGiftCardOfferedByOpencare.message}</FormError>
          )}
        </AcknowledgementsWrapper>
      );
    }

    if (offerAcknowledgementVariantNumber === '2') {
      return (
        <OfferAcknowledgementCard
          acceptsTerms={acceptsTerms}
          acceptsGiftCardOfferedByOpencare={acceptsGiftCardOfferedByOpencare}
          onAcceptTermsChange={setAcceptsTerms}
          onAcceptGiftCardOfferedByOpencareChange={setAcceptsGiftCardOfferedByOpencare}
          showValidationErrors={showValidationErrors}
          formValidity={formValidity}
          onEligibilityRequirementsClick={() => setOpencareRewardsModalVisible(true)}
        />
      );
    }

    return null;
  }, [
    showOfferAcknowledgement,
    offerAcknowledgementVariantNumber,
    offerTermsLabel,
    acceptsTerms,
    acceptsGiftCardOfferedByOpencare,
    showValidationErrors,
    formValidity,
  ]);

  return (
    <>
      <ConfirmStepWrapper $hasDisclaimer={isSecurityMessagingVariant}>
        <StepHeader>
          <StepHeaderText>Review your Request</StepHeaderText>
          {!isSecurityMessagingVariant && (
            <StepHeaderSubtext>
              Please ensure the information below is
              correct before submission to your new dentist
            </StepHeaderSubtext>
          )}
        </StepHeader>

        <DetailsWrapper>
          {showPreferredAppointmentTime
            && (
              <DetailItem
                label={preferredAppointmentLabel}
                details={[
                  { value: formattedTimeSlot, isSensitive: true },
                  { value: requestedAppointmentTypeName, isSensitive: true },
                ]}
              />
            )}

          <DetailItem label="Practice">
            <PracticeDetails>
              <PracticeTextWrapper>
                <Details
                  details={[
                    { value: nameToShow, isSensitive: false },
                    // Show practice name if provider is not selected
                    ...(showProvider ? [{ value: practiceName, isSensitive: false }] : []),
                    { value: formattedAddress, isSensitive: false },
                  ]}
                />
                <StatusBadgesWrapper>
                  {isClinicUS && <InsuranceStatusBadge inNetwork={isInNetwork} />}
                  {!isOfferAllowed && <NoOffersStatusBadge />}
                </StatusBadgesWrapper>
              </PracticeTextWrapper>

              <ProfilePhoto src={profilePhotoUrl} alt="" />
            </PracticeDetails>
          </DetailItem>

          <DetailItem
            label="Contact Details"
            details={[
              { value: patientName, isSensitive: true },
              { value: String(patient.email || ''), isSensitive: true },
              { value: formattedPhoneNumber, isSensitive: true },
            ]}
          />

          <DetailItem label="I prefer to be contacted via:" gap={12}>
            <FormRow>
              {contactOptions.map((option) => (
                <CheckboxLabeledInput key={option.contactMethod} label={option.label}>
                  <FormCheckbox
                    checked={preferredContactMethods[option.contactMethod]}
                    onChange={() => togglePreferredContactMethod(option.contactMethod)}
                  />
                </CheckboxLabeledInput>
              ))}
            </FormRow>

            <Input
              type={InputType.TEXTAREA}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Notes for the practice"
            />
          </DetailItem>
        </DetailsWrapper>

        <SubmitWrapper>
          {shouldShowPromoCodeBox && <PromoCodeBox />}

          {renderOfferAndAcknowledgement()}

          {!isOfferAllowed && (
            <AcknowledgementsWrapper>
              {/* If we don't use htmlFor, the label will be associated with the LinkButton in the label */}
              <CheckboxLabeledInput label={noOfferTermsLabel}>
                <FormCheckbox
                  id="no-offer-terms"
                  checked={acceptsNoOffer}
                  onChange={(e) => setAcceptsNoOffer(e.target.checked)}
                />
              </CheckboxLabeledInput>
              {showValidationErrors && formValidity.acceptsNoOffer?.message && (
                <FormError>{formValidity.acceptsNoOffer.message}</FormError>
              )}
            </AcknowledgementsWrapper>
          )}

          <DisclaimerText>
            By requesting an appointment with Opencare, you agree to
            our <TermsOfServiceLink /> and <PrivacyLink />.
          </DisclaimerText>

          <ErrorMessage
            requestAppointmentError={requestAppointmentError}
            validateSlotError={validateSlotError}
          />
          <SecurityBadgesLogos />

          <SubmitRequestWrapper $hasDisclaimer={isSecurityMessagingVariant}>
            <SubmitRequestButton
              onClick={handleSubmit}
              $loading={isSubmitLoading}
              $disabled={!formValidity.allValid}
            >
              Send Request
            </SubmitRequestButton>
            {isSecurityMessagingVariant && (
              <DisclaimerText style={{ textAlign: 'center' }}>
                Free booking. No cancellation fees. Your request will be sent securely.
              </DisclaimerText>
            )}
          </SubmitRequestWrapper>
        </SubmitWrapper>
      </ConfirmStepWrapper>

      <NoAvailabilityModal
        isOpen={isNoAvailabilityModalOpen}
        onClose={() => setIsNoAvailabilityModalOpen(false)}
      />

      {showOfferAcknowledgement && (
        <OpencareRewardsModal
          offerName={offerName}
          isThirdPartyOffer={isThirdPartyOffer}
          opencareRewardsModalVisible={opencareRewardsModalVisible}
          setOpencareRewardsModalVisible={setOpencareRewardsModalVisible}
        />
      )}
    </>
  );
};
